import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCodigoUsuarioInfoConcesion,
  getConcesionPorCodigoInfoConcesion,
  getComerciosConsultaInfoConcesion,
} from "modules/finops/services/infoConcesionService";
import { useAuth } from "app/providers/authContext";
import { codigoConcesionUsuario, identidadUsuarioConcesion, rolUsuarioConcesion } from "./scope";
import { consultarLlavesConcesion } from "./conciliationKeys";
const mensajeError = (error) =>
  error.response?.data?.message || error.message || "No se pudo completar la solicitud.";

export default function useInfoConcesion() {
  const { user, token, isLoading: sesionCargando, hasPermission } = useAuth();
  const tieneAcceso = hasPermission?.("infoConcesion") === true;
  const rol = rolUsuarioConcesion(user);
  const puedeRegistrar =
    tieneAcceso && ["concesiones", "admin", "coordinador finops", "usuario finops"].includes(rol);
  const consultaGeneral = tieneAcceso && Boolean(rol) && rol !== "concesiones";
  const codigoSesion = codigoConcesionUsuario(user);
  const { id: usuarioId, username } = identidadUsuarioConcesion(user, token);
  const [codigoResuelto, setCodigoResuelto] = useState("");
  const [concesion, setConcesion] = useState(null);
  const [scope, setScope] = useState("");
  const cuenta = JSON.stringify([usuarioId, username, rol, tieneAcceso, codigoSesion]);
  const cuentaRef = useRef(cuenta);
  cuentaRef.current = cuenta;
  const [catalogo, setCatalogo] = useState({ cuenta: "", rows: [] });
  const [seleccion, setSeleccion] = useState({ cuenta: "", code: "" });
  const [cargandoComercios, setCargandoComercios] = useState(false);
  const [errorComercios, setErrorComercios] = useState("");
  const peticionCatalogo = useRef(null);
  const comercios = catalogo.cuenta === cuenta ? catalogo.rows : [];
  const codigoSeleccionado = seleccion.cuenta === cuenta ? seleccion.code : "";
  const seleccionada = comercios.find((item) => item.code === codigoSeleccionado);
  const scopeActual = JSON.stringify([cuenta, consultaGeneral ? codigoSeleccionado : codigoSesion]);
  const seleccionarComercio = (code) => {
    if (!consultaGeneral) return;
    if (code && !comercios.some((item) => item.code === code)) return;
    setSeleccion({ cuenta, code });
  };
  const cargarComercios = useCallback(async () => {
    peticionCatalogo.current?.abort();
    setErrorComercios("");
    if (!consultaGeneral || sesionCargando) {
      setCargandoComercios(false);
      return;
    }
    const controller = new AbortController();
    peticionCatalogo.current = controller;
    setCargandoComercios(true);
    try {
      const rows = await getComerciosConsultaInfoConcesion(controller.signal);
      if (!controller.signal.aborted && cuentaRef.current === cuenta) setCatalogo({ cuenta, rows });
    } catch (error) {
      if (!controller.signal.aborted && cuentaRef.current === cuenta)
        setErrorComercios(mensajeError(error));
    } finally {
      if (!controller.signal.aborted && cuentaRef.current === cuenta) setCargandoComercios(false);
    }
  }, [cuenta, consultaGeneral, sesionCargando]);
  useEffect(() => {
    cargarComercios();
    return () => peticionCatalogo.current?.abort();
  }, [cargarComercios]);
  const codigo = scope === scopeActual ? codigoResuelto : "";
  const scopeRef = useRef(scopeActual);
  scopeRef.current = scopeActual;
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [progreso, setProgreso] = useState({ done: 0, total: 0 });
  const peticion = useRef(null);
  const codigoCuenta = useRef(null);
  const mounted = useRef(false);

  const cargar = useCallback(async () => {
    peticion.current?.abort();
    setCodigoResuelto("");
    setConcesion(null);
    setRegistros([]);
    setScope(scopeActual);
    setError("");
    setAviso("");
    setProgreso({ done: 0, total: 0 });
    if (sesionCargando) {
      setLoading(true);
      return;
    }
    if (!tieneAcceso || !rol) {
      setLoading(false);
      setError(
        !tieneAcceso
          ? "No tienes acceso a Información de concesión."
          : "No se pudo verificar el rol de tu sesión."
      );
      return;
    }
    if (consultaGeneral && !seleccionada) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    peticion.current = controller;
    setLoading(true);
    const vigente = () =>
      !controller.signal.aborted && mounted.current && scopeRef.current === scopeActual;
    try {
      let codigo = consultaGeneral ? seleccionada.code : codigoSesion;
      if (!codigo) {
        const cached = codigoCuenta.current;
        codigo =
          cached?.cuenta === cuenta && cached.token === token
            ? cached.code
            : await getCodigoUsuarioInfoConcesion({ id: usuarioId, username }, controller.signal);
        if (!vigente()) return;
        codigoCuenta.current = { cuenta, token, code: codigo };
      }
      if (!vigente()) return;
      setCodigoResuelto(codigo);
      const encontrada = consultaGeneral
        ? seleccionada
        : await getConcesionPorCodigoInfoConcesion(codigo, controller.signal);
      if (!vigente()) return;
      setConcesion(encontrada);
      const resultado = await consultarLlavesConcesion(codigo, {
        signal: controller.signal,
        onProgress: (value) => {
          if (vigente()) setProgreso(value);
        },
      });
      if (!vigente()) return;
      setRegistros(resultado.rows);
      if (resultado.failed.length)
        setAviso(
          `No se pudieron completar los resúmenes de ${resultado.failed.length} de ${resultado.total} llaves. Algunas cantidades no están disponibles; pulsa Actualizar para reintentar.`
        );
    } catch (err) {
      if (vigente()) {
        setError(mensajeError(err));
        setLoading(false);
        controller.abort();
      }
    } finally {
      if (vigente()) setLoading(false);
    }
  }, [
    codigoSesion,
    usuarioId,
    username,
    scopeActual,
    sesionCargando,
    tieneAcceso,
    rol,
    consultaGeneral,
    seleccionada,
    cuenta,
    token,
  ]);

  useEffect(() => {
    mounted.current = true;
    cargar();
    return () => {
      mounted.current = false;
      peticion.current?.abort();
    };
  }, [cargar]);

  return {
    puedeRegistrar,
    consultaGeneral,
    comercios,
    codigoSeleccionado,
    seleccionarComercio,
    cargandoComercios,
    errorComercios,
    cargarComercios,
    codigo,
    concesion: scope === scopeActual ? concesion : null,
    registros: scope === scopeActual ? registros : [],
    loading,
    error,
    aviso,
    progreso,
    cargar,
    scope: scopeActual,
  };
}
